// varSelectOptions — the option list behind every variable dropdown in the
// graph editors (Set Variable, Get Variable, the array/map ops).
//
// Pure so it can be unit-tested directly; the component that renders it
// (components/nodes/VarSelect.jsx) is a thin `<select>` around this.
//
// The rule that is easy to get wrong, and is why this exists as its own module:
// **a name the node already holds is never silently dropped from the list.** A
// dropdown that simply filters shows "— pick var —" over a node whose data says
// `ConversationalState`, so the graph and the UI disagree with nothing on screen
// to say so, and the next edit writes the empty value over a working reference.
// An unlistable current value is therefore appended as a DISABLED option naming
// why it cannot be picked — declared as the wrong type, or not declared at all.
//
// The type suffix is shown only on an UNFILTERED list. A filtered one (the array
// / map ops) has exactly one type by construction, so "(array)" on every row is
// noise.

/**
 * @param {Array<{id?:string,name:string,type:string}>} variables  declared variables
 * @param {{filterType?:string, current?:string}} [opts]
 *   filterType — keep only variables of this type; omit for all
 *   current    — the value the node holds right now
 * @returns {{options:Array<{value:string,label:string,disabled:boolean}>,
 *            problem:string|null}}
 *   `problem` is the reason `current` is unlistable, or null.
 */
export function buildVarOptions(variables, opts = {}) {
  const { filterType, current } = opts
  const all = Array.isArray(variables) ? variables : []
  const listed = filterType ? all.filter(v => v?.type === filterType) : all

  const options = [{ value: '', label: '— pick var —', disabled: false }]
  for (const v of listed) {
    if (!v?.name) continue
    options.push({
      value: v.name,
      label: filterType ? v.name : `${v.name} (${v.type ?? '?'})`,
      disabled: false,
    })
  }

  const name = typeof current === 'string' ? current : ''
  if (!name || options.some(o => o.value === name)) return { options, problem: null }

  // Held but unlistable — say which, rather than showing an empty field.
  const declared = all.find(v => v?.name === name)
  const problem = declared
    ? `is ${declared.type}, not ${filterType}`
    : 'not declared'
  options.push({ value: name, label: `${name} — ${problem}`, disabled: true })
  return { options, problem }
}
